import { useState, useCallback, useMemo, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import {
  useGetTemporaryDocListQuery,
  useDeleteTemporaryDocMutation,
  temporaryDocDetailQueryOptions,
  documentReceiverListQueryOptions,
  documentExecutorListQueryOptions,
  documentReferenceDocsQueryOptions,
  documentFileListQueryOptions,
  employeeInfoQueryOptions,
} from '@features/archive-box/hooks';

import { useDocFormListSimpleQuery } from '@features/form-selection/hooks';
import useGetTeamQuery from '@features/team/hooks';
import useAuthStore from '@entities/auth/model/useAuthStore';

import SearchFilter from '@shared/components/organisms/SearchFilter/SearchFilter';
import ApprovalDataTable from '@shared/components/organisms/ApprovalDataTable/ApprovalDataTable';
import Button from '@shared/components/atoms/Button';
import Pagination from '@shared/components/organisms/Pagination/Pagination';
import ContentCard from '@shared/components/organisms/ContentCard/ContentCard';
import MainLayout from '@shared/components/organisms/MainLayout/MainLayout';
import TabContentLayout from '@shared/components/organisms/TabContentLayout/TabContentLayout';

import useSort from '@shared/hooks/useSort';
import { getDefaultDateRange, formatDateToYYYYMMDD } from '@shared/utils/formatDate';

import styles from './TemporaryArchivePage.module.scss';

/**
 * TemporaryArchivePage (임시보관함)
 * - 사용자가 작성 중 임시저장한 문서 목록을 조회하고 관리하는 페이지
 */
function TemporaryArchivePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const companyCode = useAuthStore((state) => state.companyCode);

  // 기본 조회 기간 설정 (최근 3개월)
  const defaultRange = useMemo(() => getDefaultDateRange(), []);

  // 문서유형 목록 조회
  const { data: docFormList = [] } = useDocFormListSimpleQuery(companyCode);

  // 기안부서 목록 조회
  const { data: teamResponse } = useGetTeamQuery(companyCode);

  // 필터 상태 관리
  const [filterValues, setFilterValues] = useState({
    queryPeriod_start: defaultRange.start,
    queryPeriod_end: defaultRange.end,
    documentStatus: '전체',
    documentType: '전체',
    documentTitle: '',
  });

  // 검색 파라미터 상태 (실제 API 요청에 사용)
  const [searchParams, setSearchParams] = useState({
    date1: defaultRange.start,
    date2: defaultRange.end,
  });

  // 선택된 행 ID 목록
  const [selectedRows, setSelectedRows] = useState([]);

  /** 임시저장 문서 목록 조회 */
  const {
    data: temporaryDocResponse,
    refetch: refetchTemporaryDocList,
    isLoading,
    isFetching,
  } = useGetTemporaryDocListQuery(searchParams);

  // 라우터 state에 refetch 요청이 있으면 목록을 갱신 (임시저장 후 진입 시)
  useEffect(
    function () {
      if (location.state?.refetch) {
        refetchTemporaryDocList();
        // refetch 상태를 초기화하여 불필요한 재로딩 방지
        navigate(location.pathname, { replace: true, state: {} });
      }
    },
    [location.state?.refetch, refetchTemporaryDocList, navigate, location.pathname],
  );

  /** 임시저장 문서 삭제 Mutation */
  const { mutateAsync: deleteTemporaryDoc } = useDeleteTemporaryDocMutation({
    onSuccess: () => {
      refetchTemporaryDocList();
    },
  });

  /** 테이블 데이터 매핑 */
  const tableData = useMemo(() => {
    const docInfoList = temporaryDocResponse?.resultData?.docInfoList;
    if (!docInfoList) return [];

    return docInfoList.map(function (item) {
      let formattedDate = item.date;
      if (item.date && item.date.length >= 12) {
        const yyyy = item.date.substring(0, 4);
        const mm = item.date.substring(4, 6);
        const dd = item.date.substring(6, 8);
        const hh = item.date.substring(8, 10);
        const min = item.date.substring(10, 12);
        formattedDate = `${yyyy}년${mm}월${dd}일 ${hh}시${min}분`;
      } else if (item.date && item.date.length >= 10) {
        // 10자리 이상이지만 12자리 미만인 경우 (시까지만)
        const yyyy = item.date.substring(0, 4);
        const mm = item.date.substring(4, 6);
        const dd = item.date.substring(6, 8);
        const hh = item.date.substring(8, 10);
        formattedDate = `${yyyy}년${mm}월${dd}일 ${hh}시`;
      }

      return {
        id: item.idx,
        docType: item.docType,
        title: item.title,
        date: formattedDate,
        rawDate: item.date, // 정렬용 원본 날짜
      };
    });
  }, [temporaryDocResponse]);

  /** 양식명 목록 추출 (필터 옵션용) - 전체 양식 목록에서 가져오기 */
  const formNameOptions = useMemo(() => {
    if (!docFormList || docFormList.length === 0) return ['전체'];

    // 전체 양식 목록에서 양식명 추출
    const formNames = docFormList.map((item) => item.title).filter((name) => name && name.trim());
    // 알파벳 순으로 정렬
    formNames.sort();
    // "전체"를 맨 앞에 추가
    return ['전체', ...formNames];
  }, [docFormList]);

  /** 기안부서 목록 추출 (필터 옵션용) - 전체 부서 목록에서 가져오기 */
  const teamOptions = useMemo(() => {
    const teamList = Array.isArray(teamResponse?.resultData?.teamList)
      ? teamResponse.resultData.teamList
      : [];

    // 전체 부서 목록에서 부서명 추출
    const teamNames = teamList.map((item) => item.team).filter((name) => name && name.trim());
    // 알파벳 순으로 정렬
    teamNames.sort();
    // "전체"를 맨 앞에 추가
    return ['전체', ...teamNames];
  }, [teamResponse]);

  // 양식명 필터 적용
  const filteredData = useMemo(() => {
    if (!filterValues.documentType || filterValues.documentType === '전체') {
      return tableData;
    }
    return tableData.filter((item) => item.docType === filterValues.documentType);
  }, [tableData, filterValues.documentType]);

  // 필터 초기화 핸들러
  const handleReset = useCallback(() => {
    setFilterValues({
      queryPeriod_start: defaultRange.start,
      queryPeriod_end: defaultRange.end,
      documentStatus: '전체',
      documentType: '전체',
      documentTitle: '',
    });
  }, [defaultRange]);

  // 정렬 훅 사용
  const { sortedData, sortField, sortDirection, handleSort } = useSort(filteredData, {
    field: 'date',
    direction: 'desc',
  });

  /** 페이지네이션 관련 상태 및 데이터 */
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 15;

  // 페이지네이션 적용
  const totalPages = Math.ceil(sortedData.length / pageSize);
  const paginatedData = useMemo(() => {
    return sortedData.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  }, [sortedData, currentPage, pageSize]);

  /** 데이터 없음 여부 판단 (로딩 중일 때는 false 유지) */
  const isEmpty = !isLoading && !isFetching && sortedData.length === 0;

  // 전체 선택 여부
  const isAllSelected = useMemo(() => {
    return (
      paginatedData.length > 0 && paginatedData.every((item) => selectedRows.includes(item.id))
    );
  }, [paginatedData, selectedRows]);

  // 전체 선택/해제 핸들러
  function handleSelectAll() {
    if (isAllSelected) {
      // 현재 페이지 항목들을 선택 해제
      const currentPageIds = paginatedData.map((item) => item.id);
      setSelectedRows((prev) => prev.filter((id) => !currentPageIds.includes(id)));
    } else {
      // 현재 페이지 항목들을 모두 선택
      const currentPageIds = paginatedData.map((item) => item.id);
      setSelectedRows((prev) => [...new Set([...prev, ...currentPageIds])]);
    }
  }

  // 필터 필드 정의 (기획 기준)
  const filterFields = useMemo(
    () => [
      // 첫 번째 행: 조회기간
      {
        key: 'queryPeriod',
        type: 'dateRange',
        label: '조회기간',
        startPlaceholder: defaultRange.start,
        endPlaceholder: defaultRange.end,
        startKey: 'queryPeriod_start',
        endKey: 'queryPeriod_end',
        startValue: filterValues.queryPeriod_start || '',
        endValue: filterValues.queryPeriod_end || '',
      },
      { newRow: true },
      // 두 번째 행: 양식명
      {
        key: 'documentType',
        type: 'select',
        label: '문서유형',
        value: filterValues.documentType || '전체',
        options: formNameOptions,
      },
      { newRow: true },
      // 세 번째 행: 문서제목
      {
        key: 'documentTitle',
        type: 'input',
        label: '문서제목',

        placeholder: '제목을 입력해 주세요.',
        value: filterValues.documentTitle || '',
        wide: true,
      },
    ],
    [defaultRange, filterValues, formNameOptions],
  );

  // 테이블 컬럼 정의 (기획 기준: 체크박스, 문서분류, 문서제목, 작성일)
  const tableColumns = useMemo(
    () => [
      {
        key: 'checkbox',
        label: '',
        width: '60px',
        sortable: false,
        stopPropagation: true,
        headerRender() {
          return (
            <input
              type="checkbox"
              checked={isAllSelected}
              onChange={handleSelectAll}
              onClick={function (e) {
                e.stopPropagation();
              }}
              aria-label="전체 선택"
            />
          );
        },
        render(value, item) {
          const isSelected = selectedRows.includes(item.id);
          return (
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => {
                if (isSelected) {
                  setSelectedRows((prev) => prev.filter((id) => id !== item.id));
                } else {
                  setSelectedRows((prev) => [...prev, item.id]);
                }
              }}
              onClick={function (e) {
                e.stopPropagation();
              }}
              aria-label="선택"
            />
          );
        },
      },
      {
        key: 'docType',
        label: '문서유형',
        width: '180px',
        sortable: true,
      },
      {
        key: 'title',
        label: '문서제목',
        sortable: true,
        width: '500px',
        textAlign: 'left',
      },
      {
        key: 'date',
        label: '작성일',
        width: '220px',
        sortable: true,
      },
    ],
    [isAllSelected, selectedRows],
  );

  // 필터 변경 시 호출되는 공통 핸들러
  const handleFilterChange = useCallback(function (key, value) {
    setFilterValues(function (prev) {
      return { ...prev, [key]: value };
    });
  }, []);

  // 검색/조회 버튼 클릭 핸들러
  const handleSearch = useCallback(
    function () {
      setSearchParams({
        date1: filterValues.queryPeriod_start,
        date2: filterValues.queryPeriod_end,
        title: filterValues.documentTitle?.trim() || undefined,
      });
      setCurrentPage(1);
    },
    [filterValues],
  );

  // 선택 삭제 핸들러
  const handleDelete = useCallback(
    async function () {
      if (selectedRows.length === 0) {
        alert('삭제할 문서를 선택해 주세요.');
        return;
      }

      if (!confirm('선택한 문서를 삭제하시겠습니까?')) {
        return;
      }

      try {
        const deletePromises = selectedRows.map((id) => deleteTemporaryDoc(id));
        await Promise.all(deletePromises);

        // 삭제 성공 후 선택 항목 초기화
        setSelectedRows([]);

        alert('삭제가 완료되었습니다.');
      } catch (error) {
        console.error('문서 삭제 실패:', error);
        alert('삭제 중 오류가 발생했습니다.');
      }
    },
    [selectedRows, deleteTemporaryDoc],
  );

  const handlePageChange = useCallback(function (page) {
    setCurrentPage(page);
  }, []);

  // 결재라인 필드 매핑 함수 (문서 상세 transformDocumentData와 동일하게 order 보존)
  async function mapApprovalLineField(enumber, state, order) {
    if (!enumber || enumber === 0) return null;
    const numericState = state != null ? Number(state) : 0;
    const numericOrder = order != null && order !== '' ? Number(order) : 0;

    try {
      const response = await queryClient.fetchQuery(employeeInfoQueryOptions(Number(enumber)));
      if (response && response.result_code === 0 && response.resultData) {
        const employee = response.resultData;
        return {
          enumber: enumber,
          name: employee.name || '',
          position: employee.position || '',
          team: employee.team || '',
          state: numericState,
          order: numericOrder || 0,
        };
      }
    } catch (error) {
      console.error('직원 정보 조회 실패:', enumber, error);
    }
    return {
      enumber: enumber,
      name: '',
      position: '',
      team: '',
      state: numericState,
      order: numericOrder || 0,
    };
  }

  // 문서 클릭 핸들러 - 문서 작성 페이지로 이동
  const handleRowClick = useCallback(
    async function (item) {
      try {
        // 임시저장 문서 상세 조회 및 관련 데이터 병렬 조회 (queryClient.fetchQuery 사용)
        const [docResponse, receiverResponse, executorResponse, referenceResponse, fileResponse] =
          await Promise.all([
            queryClient.fetchQuery(temporaryDocDetailQueryOptions(item.id)),
            queryClient.fetchQuery(documentReceiverListQueryOptions(item.id)).catch(() => null),
            queryClient.fetchQuery(documentExecutorListQueryOptions(item.id)).catch(() => null),
            queryClient.fetchQuery(documentReferenceDocsQueryOptions(item.id)).catch(() => null),
            queryClient.fetchQuery(documentFileListQueryOptions(item.id)).catch(() => null),
          ]);

        if (docResponse && docResponse.result_code === 0 && docResponse.resultData) {
          const docData = docResponse.resultData;

          // 결재라인 매핑 (병렬 조회)
          const approvalLinePromises = [
            mapApprovalLineField(docData.sign_1, docData.sign_1_state, docData.sign_1_order), // 기안자
            mapApprovalLineField(docData.sign_2, docData.sign_2_state, docData.sign_2_order), // 결재자1
            mapApprovalLineField(docData.sign_3, docData.sign_3_state, docData.sign_3_order), // 결재자2
            mapApprovalLineField(docData.sign_4, docData.sign_4_state, docData.sign_4_order), // 결재자3
            mapApprovalLineField(docData.sign_5, docData.sign_5_state, docData.sign_5_order), // 결재자4
            mapApprovalLineField(docData.check_1, docData.check_1_state, docData.check_1_order), // 검토자1
            mapApprovalLineField(docData.check_2, docData.check_2_state, docData.check_2_order), // 검토자2
            mapApprovalLineField(docData.check_3, docData.check_3_state, docData.check_3_order), // 검토자3
            mapApprovalLineField(docData.check_4, docData.check_4_state, docData.check_4_order), // 검토자4
            mapApprovalLineField(
              docData.agreement,
              docData.agreement_state,
              docData.agreement_order,
            ), // 합의자
          ];

          const [
            draft1,
            approval1,
            approval2,
            approval3,
            approval4,
            review1,
            review2,
            review3,
            review4,
            agreement1,
          ] = await Promise.all(approvalLinePromises);

          // 수신자 매핑
          const receiverList =
            receiverResponse?.resultData?.docReceiverList || receiverResponse?.resultData || [];
          const recipients = await Promise.all(
            receiverList
              .filter((r) => r != null && r.enumber != null)
              .map(async (r) => {
                try {
                  const empResponse = await queryClient.fetchQuery(
                    employeeInfoQueryOptions(Number(r.enumber)),
                  );
                  const employee = empResponse?.resultData || {};
                  return {
                    enumber: r.enumber,
                    name: employee.name || '',
                    position: employee.position || '',
                    team: employee.team || '',
                  };
                } catch (error) {
                  return { enumber: r.enumber, name: '', position: '', team: '' };
                }
              }),
          );

          // 시행자 매핑
          const executorList =
            executorResponse?.resultData?.docExecutorList || executorResponse?.resultData || [];
          const validExecutors = await Promise.all(
            executorList
              .filter((e) => e != null && e.enumber != null)
              .map(async (e) => {
                try {
                  const empResponse = await queryClient.fetchQuery(
                    employeeInfoQueryOptions(Number(e.enumber)),
                  );
                  const employee = empResponse?.resultData || {};
                  return {
                    enumber: e.enumber,
                    name: employee.name || '',
                    position: employee.position || '',
                    team: employee.team || '',
                  };
                } catch (error) {
                  return { enumber: e.enumber, name: '', position: '', team: '' };
                }
              }),
          );

          // 참조문서 매핑 (API 응답 구조: resultData.referenceDocInfoList)
          const referenceListData =
            referenceResponse?.resultData?.referenceDocInfoList ||
            referenceResponse?.resultData?.docReferenceList ||
            referenceResponse?.resultData;
          const referenceDocumentsRaw = Array.isArray(referenceListData) ? referenceListData : [];

          // 참조문서 변환: idx 필드 보장 (API 응답의 docIdx를 idx로 변환)
          const referenceDocuments = referenceDocumentsRaw.map((ref) => {
            if (typeof ref === 'object' && ref !== null) {
              return {
                ...ref,
                // API 응답: docIdx → 변환: idx
                idx: ref.docIdx || ref.idx || ref.document_idx || ref.id || 0,
                // 참조문서 표시용 필드 보장
                title: ref.title || ref.doc_title || ref.name || '',
                sender: ref.docUser || ref.name || ref.drafter || '',
                drafter: ref.docUser || ref.drafter || ref.name || '',
              };
            }
            return ref;
          });

          // 첨부파일 매핑 (API 응답 구조: resultData.docFileInfoList)
          const fileListData =
            fileResponse?.resultData?.docFileInfoList ||
            fileResponse?.resultData?.docFileList ||
            fileResponse?.resultData;
          const attachmentsRaw = Array.isArray(fileListData) ? fileListData : [];

          // 첨부파일 변환: idx 필드 보장 (API 응답의 idx는 이미 있음)
          const attachments = attachmentsRaw.map((file) => {
            if (typeof file === 'object' && file !== null) {
              return {
                ...file,
                // API 응답: idx → 그대로 사용
                idx: file.idx || file.fileIdx || file.file_idx || file.id || 0,
                // 첨부파일 표시용 필드 보장
                name: file.name || file.fileName || '',
                size: file.size || file.fileSize || 0,
                url: file.url || file.fileURL || '',
              };
            }
            return file;
          });

          // 임시보관함 문서를 문서 작성 페이지로 이동 (수정 모드)
          // useDocumentActions.js의 handleModify와 동일한 구조로 전달
          navigate('/approval/new-document/write', {
            state: {
              docIdx: Number(item.id),
              isEditMode: true,
              // 기존 문서 상태 (재상신 시 백엔드에서 사용)
              // 임시저장 문서는 항상 state가 0이므로 명시적으로 0으로 설정
              docState: 0,
              // 양식 정보 (useDocumentActions.js의 handleModify와 동일한 구조)
              // formName: 양식명 (docData.title), formId: 양식 ID (docData.docType 또는 docData.doc_type)
              formInfo: {
                formName: docData.title || item.docType || '',
                formId: docData.docType || docData.doc_type || docData.doc || null,
                formContent: docData.contents || '',
              },
              // 문서 정보
              documentInfo: {
                id: item.id,
                formCode: docData.doc,
                formName: item.docType || docData.docType || '',
                retentionPeriod: docData.keep_year ? `${docData.keep_year}년` : '5년',
                documentNumber: docData.doc_number || '',
                draftingDepartment: docData.team || '',
                drafter: docData.name || '',
                // 기안일자: docData.date를 yyyy-MM-dd 형식으로 변환
                draftDate: docData.date
                  ? formatDateToYYYYMMDD(docData.date) !== '-'
                    ? formatDateToYYYYMMDD(docData.date)
                    : new Date().toISOString().split('T')[0]
                  : new Date().toISOString().split('T')[0],
                recipients: recipients,
                executors: validExecutors,
                subject: docData.doc_title || '',
                approvalLine: {
                  draft1,
                  approval1,
                  approval2,
                  approval3,
                  approval4,
                  review1,
                  review2,
                  review3,
                  review4,
                  agreement1,
                },
              },
              // 에디터 내용
              editorContent: docData.contents || '',
              // 참조문서
              referenceDocuments: referenceDocuments,
              // 첨부파일
              attachments: attachments,
              // 요약 및 비고
              summaryNote: docData.summary_note || docData.summary || docData.note || '',
              // 법인 인감 사용 여부
              useCorporateSeal:
                docData.is_corp_seal_used === 1 ||
                docData.is_corp_seal_used === '1' ||
                docData.isCorpSealUsed === 1 ||
                docData.isCorpSealUsed === '1',
            },
          });
        } else {
          alert('문서를 불러올 수 없습니다.');
        }
      } catch (error) {
        console.error('임시저장 문서 조회 실패:', error);
        alert('문서를 불러오는 중 오류가 발생했습니다.');
      }
    },
    [navigate, queryClient],
  );

  return (
    <MainLayout>
      <TabContentLayout
        tabs={{
          label: '임시보관함',
          content: (
            <ContentCard className={styles.contentCardMarginFix}>
              {/* 검색/필터 섹션 */}
              <div className={styles.filterWrapper}>
                <SearchFilter
                  fields={filterFields}
                  onFilterChange={handleFilterChange}
                  onSearch={handleSearch}
                  onReset={handleReset}
                  showExcelButton={false}
                />
              </div>
              <div className={styles.buttonWrap}>
                <Button type="button" className={styles.deleteButton} onClick={handleDelete}>
                  선택 삭제
                </Button>
              </div>
              {/* 데이터 테이블 */}
              <div className={styles.tableWrapper}>
                {isLoading || (isFetching && tableData.length === 0) ? (
                  <div className={styles.loadingContainer}>
                    <div className={styles.circularProgress} />
                    <div className={styles.loadingMessage}>문서 목록을 불러오는 중입니다...</div>
                  </div>
                ) : (
                  <>
                    <ApprovalDataTable
                      columns={tableColumns}
                      data={paginatedData}
                      onRowClick={handleRowClick}
                      onSort={handleSort}
                      sortField={{ field: sortField, direction: sortDirection }}
                      emptyContent="임시저장 문서가 없습니다."
                    />
                    {/* {isEmpty && <div className={styles.emptyCell}>임시저장 문서가 없습니다.</div>} */}
                  </>
                )}
              </div>

              {/* 페이지네이션 + 선택 삭제 버튼 */}
              <div className={styles.paginationRow}>
                <div className={styles.paginationWrapper}>
                  {paginatedData.length > 0 && (
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={handlePageChange}
                    />
                  )}
                </div>
              </div>
            </ContentCard>
          ),
        }}
      />
    </MainLayout>
  );
}

export default TemporaryArchivePage;
